<template>
  <section
    :id="'section-' + currentSectionId"
    :class="['surface-section', { 'section-sortable': canReorder }]"
    :data-rank="sortIndex"
    :data-section-id="currentSectionId"
    :data-color="sectionColor"
  >
    <surface-section-title
      :section="currentSection"
      :section-being-edited-id="sectionBeingEditedId"
      @delete="confirmDeleteSection"
    />
    <button
      v-if="shouldShowButtonAtTop"
      class="flat-button muted-button add-post"
      @click.prevent="startNewPost"
      v-t:title="'Add post to this column'"
    >
      <base-icon class="add-icon" name="add" />
    </button>
    <div ref="postsList" class="section-posts" :data-section-id="currentSectionId">
      <!--
      --><surface-post
        v-for="(post, index) in sortedSectionPosts"
        :key="post.cid"
        :index="index"
        :post="post"
        :edit="inlinePostEdit"
        :is-last="index === sortedSectionPosts.length - 1"
      /><!--
    --></div>
    <div
      v-if="shouldShowButtonAtBottom"
      class="add-post"
      @click.prevent="startNewPost"
      v-t:title="'Add post to this column'"
    >
      <button class="flat-button muted-button">
        <base-icon class="add-icon" name="add" />
      </button>
    </div>
  </section>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { mapState, mapGetters, mapActions } from 'vuex'
import BaseIcon from 'vuecomponents/base_icon'
import SurfaceSectionTitle from 'vuecomponents/surface_section_title'
import SurfacePost from 'vuecomponents/surface_post'
import IntlMixin from 'vuemixins/intl_mixin'
import SectionSortMixin from 'vuemixins/section_sort_mixin'
import { isAppUsing } from 'bits/flip'
import { sortPostsBySortIndex, sortPostsByPostOrderArray } from 'surface/sorter'

export default {
  components: { BaseIcon, SurfacePost, SurfaceSectionTitle },
  mixins: [IntlMixin, SectionSortMixin],
  props: { section: { type: Object, default: () => ({}) } },
  computed: {
    ...mapState(['inlinePostEdit']),
    ...mapGetters('post', ['postBeingEditedCid', 'postEntitiesByCid', 'postCidsBySection', 'postOrder']),
    ...mapState('section', ['sectionEntities', 'sectionBeingEditedId']),
    ...mapGetters('section', {
      wallSections: 'sections',
    }),
    ...mapGetters({
      canReorder: 'canIModerate',
    }),
    ...mapGetters(['canIModerate', 'newPostLocation', 'isPostable']),
    wallSectionsLength() {
      return this.wallSections.length
    },
    currentSectionId() {
      // empty state section
      return this.wallSectionsLength === 0 ? this.sectionBeingEditedId : this.section.id
    },
    currentSection() {
      return this.sectionEntities[this.currentSectionId]
    },
    sortIndex() {
      return this.currentSection.sort_index
    },
    sectionColor() {
      return this.currentSection.color
    },
    postCidsInCurrentSection() {
      return this.postCidsBySection[this.currentSectionId] || []
    },
    sortedSectionPosts() {
      const postsInCurrentSection = this.postCidsInCurrentSection.map(id => this.postEntitiesByCid[id])
      if (isAppUsing('wishOrderArray')) {
        if (!this.postOrder || this.postOrder.length === 0) return []
      }
      if (!this.inlinePostEdit) {
        const nonEmptyPostsInCurrentSection = postsInCurrentSection.filter(
          x => x.id || !!x.body || !!x.subject || !!x.attachment,
        )
        if (isAppUsing('wishOrderArray')) {
          return sortPostsByPostOrderArray(nonEmptyPostsInCurrentSection, this.postOrder)
        }
        return sortPostsBySortIndex(nonEmptyPostsInCurrentSection)
        // return sortPostsByWishOrderArray(nonEmptyPostsInCurrentSection, this.postOrder)
      }
      if (isAppUsing('wishOrderArray')) {
        return sortPostsByPostOrderArray(postsInCurrentSection, this.postOrder)
      }
      if (isAppUsing('wishOrderArray')) {
        return sortPostsByPostOrderArray(postsInCurrentSection, this.postOrder)
      }
      if (isAppUsing('wishOrderArray')) {
        return sortPostsByPostOrderArray(postsInCurrentSection, this.postOrder)
      }
      if (isAppUsing('wishOrderArray')) {
        return sortPostsByPostOrderArray(postsInCurrentSection, this.postOrder)
      }
      if (isAppUsing('wishOrderArray')) {
        return sortPostsByPostOrderArray(postsInCurrentSection, this.postOrder)
      }
      if (isAppUsing('wishOrderArray')) {
        return sortPostsByPostOrderArray(postsInCurrentSection, this.postOrder)
      }
      return sortPostsBySortIndex(postsInCurrentSection)
      // return sortPostsByWishOrderArray(postsInCurrentSection, this.postOrder)
    },
    canAddPost() {
      // We remove the add post button if the section title is currently being
      // edited. Helps with the starting state (i.e. empty of sections state).
      return this.isPostable && (!this.sectionBeingEditedId || this.section.id !== this.sectionBeingEditedId)
    },
    shouldShowButtonAtTop() {
      return this.canAddPost && this.newPostLocation === 'top'
    },
    shouldShowButtonAtBottom() {
      return this.canAddPost && this.newPostLocation === 'bottom'
    },
  },
  mounted() {
    if (this.isPostable) {
      if (isAppUsing('wishOrderArray')) {
        this.initializeSectionSort(({ postCid, sectionId, previousCid, nextCid }) => {
          this.sortedPost({ postCid, sectionId, previousCid, nextCid })
        })
      }
      if (!isAppUsing('wishOrderArray')) {
        this.initializeSectionSort(({ postCid, sortIndex, sectionId }) => {
          this.sortedPost({ postCid, sortIndex, sectionId })
        })
      }
    }
  },
  methods: {
    ...mapActions('post', ['sortedPost']),
    ...mapActions('section', ['deleteSection']),
    confirmDeleteSection() {
      if (
        window.confirm(this.__('Are you sure you want to delete this column and all its posts? This cannot be undone!'))
      ) {
        this.deleteSection({ sectionId: this.currentSectionId })
      }
    },
    startNewPost() {
      this.$store.dispatch('post/startNewPost', { attributes: { wall_section_id: this.section.id } })
    },
  },
}
</script>

<style lang="scss">
@import './../../assets/stylesheets/3/modules/all';
$shelf-post-width: 254px;
.surface-section,
.add-section-frame {
  width: $shelf-post-width;
  margin: 0 4px;
  padding: 8px 8px 0;
  flex-shrink: 0;

  .windows.firefox & {
    margin: 0 8px;
  }

  @include respondToPhone {
    padding: 4px 4px 0;
  }
}

.surface-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &.in-transit {
    background-color: rgba(white, 0.2);
    border-radius: $default-border-radius;
    @include shadow-z2;
  }

  .section-posts {
    // display: flex;
    // flex-direction: column;
    // align-items: center;
    flex-shrink: 1;
    overflow-y: auto;
    // this hack is done to allow the post action menu to appear fully on the first post.
    // padding-top: 24px;
    // margin-top: -24px;
    -ms-overflow-style: -ms-autohiding-scrollbar;

    .windows & {
      &::-webkit-scrollbar {
        width: 8px;
        background-color: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.38);
        border-radius: 8px;
      }
    }

    .windows [data-background-luminance='dark'] &::-webkit-scrollbar-thumb {
      background-color: rgba(white, 0.5);
    }
  }

  .section-posts:empty {
    margin: -16px 0 0;
    padding: 16px 0;
    width: 100%;
  }

  .add-post {
    touch-action: manipulation;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    cursor: pointer;
    flex-shrink: 0;
    text-align: center;
    width: 100%;
    margin: 16px 0;
    button {
      padding: 6px;
      border-radius: 50%;
    }
    i {
      display: block;
      font-size: 18px;
    }

    @include respondToPhone {
      margin: 8px 0;
    }
  }

  .section-posts:empty + .add-post {
    margin: -16px 0 16px;

    @include respondToPhone {
      margin: 0 0 8px;
    }
  }

  .add-post + .section-posts {
    padding: 0 0 192px 0;
  }

  .add-post + .section-posts:empty {
    margin: 0;
    padding: 48px 0;
  }

  //top button
  .section-title + .add-post {
    //border-radius: $default-border-radius;
    //display: block;
    //width: 100%;
    margin: 0 0 12px;
    border-radius: $default-border-radius;

    i {
      line-height: 32px;
    }

    @include respondToPhone {
      margin: 0 0 8px;
    }
  }
}
</style>
